import { Component, EventEmitter, Output, inject } from '@angular/core';
import {
    ComponentSegmentRelationAnchor,
    ComponentSegmentRelationVersion,
} from 'src/models/anchor-and-version/component-segment-relation.model';
import {
    ComponentSegmentRelationRow,
    createComponentSegmentRelationAnchorPayload,
    createComponentSegmentRelationVersionPayload,
    createNewComponentSegmentRelationRow,
    createRowsFromAnchors,
    getComponentSegmentRelationValidationMessage,
    getRowsToSave,
} from './component-segment-relation-table.utils';
import { ComponentAnchor } from 'src/models/anchor-and-version/component.model';
import { ComponentSegmentRelationService } from 'src/services/anchor-and-version-services/component-segment-relation.service';
import { ConfigState } from 'src/store/reducers/config.reducer';
import { FormsModule } from '@angular/forms';
import { NotificationService } from 'src/services/notification.service';
import { PendingChangesService } from 'src/services/pending-changes-service/pending-changes.service';
import { SegmentAnchor } from 'src/models/anchor-and-version/segment.model';
import { SegmentService } from 'src/services/anchor-and-version-services/segment.service';
import { Store } from '@ngrx/store';
import { refreshPendingChangesCount } from 'src/utils/pending-changes-count.util';

// PrimeNG
// eslint-disable-next-line sort-imports
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-component-segment-relation-table',
    templateUrl: './component-segment-relation-table.component.html',
    imports: [
        FormsModule,
        ButtonModule,
        ConfirmPopupModule,
        SelectModule,
        TableModule,
        ToggleSwitchModule,
        TooltipModule,
    ],
})
export class ComponentSegmentRelationTableComponent {
    @Output() public rowsChanged = new EventEmitter<void>();

    public isLoading = false;
    public rows: ComponentSegmentRelationRow[] = [];
    public segments: SegmentAnchor[] = [];
    public segmentsHasMore = true;
    public segmentsIsLoading = false;

    private componentSegmentRelationService = inject(ComponentSegmentRelationService);
    private confirmationService = inject(ConfirmationService);
    private notificationService = inject(NotificationService);
    private pendingChangesService = inject(PendingChangesService);
    private rowCounter = 0;
    private segmentOffset = 0;
    private segmentService = inject(SegmentService);
    private store = inject<Store<{ config: ConfigState }>>(Store);
    private readonly segmentLimit = 20;

    public addRow(): void {
        this.rows = [
            ...this.rows,
            createNewComponentSegmentRelationRow(`new-${++this.rowCounter}`),
        ];
        this.rowsChanged.emit();
    }

    public confirmRemoveUnsavedRow(row: ComponentSegmentRelationRow, event: Event): void {
        if (!row.isNew) return;

        this.confirmationService.confirm({
            target: event.target as EventTarget,
            key: 'removeComponentSegmentRelationRowPopup',
            message: 'Remove this segment relation row?',
            header: 'Confirm remove',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remove',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            rejectVisible: true,
            accept: () => this.removeUnsavedRow(row),
        });
    }

    public getSegmentLabel(segment: SegmentAnchor): string {
        return segment.validVersion?.name ?? segment.displayName ?? String(segment.key);
    }

    public getValidationMessage(): string | undefined {
        return getComponentSegmentRelationValidationMessage(this.rows);
    }

    public hasChanges(): boolean {
        const { newRows, changedRows } = getRowsToSave(this.rows);
        return newRows.length > 0 || changedRows.length > 0;
    }

    public async load(componentAnchor?: ComponentAnchor): Promise<void> {
        this.isLoading = true;
        this.rowCounter = 0;
        this.rows = [];
        this.segments = [];

        try {
            if (!componentAnchor?.key) {
                return;
            }

            await this.loadByComponentAnchorKey(componentAnchor.key);
            await this.loadSegments('');
            await this.ensureSelectedSegmentsLoaded();
        } finally {
            this.isLoading = false;
        }
    }

    public onIsActiveChange(row: ComponentSegmentRelationRow, value: boolean): void {
        row.isActive = Boolean(value);
        this.rowsChanged.emit();
    }

    public async onLoadMoreSegments(searchString: string): Promise<void> {
        this.segmentsIsLoading = true;
        this.segmentOffset += this.segmentLimit;
        try {
            const result = await this.segmentService.getAnchorsWithValidVersion({
                qDisplayName: searchString || undefined,
                qLimit: this.segmentLimit,
                qOffset: this.segmentOffset,
            });
            this.segments = [
                ...this.segments,
                ...result.map((s) => ({ ...s, displayName: this.getSegmentLabel(s) })),
            ];
            this.segmentsHasMore = result.length === this.segmentLimit;
        } catch (error) {
            this.notificationService.handleError('Failed to load more segments', error);
        } finally {
            this.segmentsIsLoading = false;
        }
    }

    public async onSearchSegments(searchString: string): Promise<void> {
        this.segmentOffset = 0;
        await this.loadSegments(searchString);
    }

    public onSegmentChange(row: ComponentSegmentRelationRow, value: number | null): void {
        row.segmentAnchorKey = value;
        this.rowsChanged.emit();
    }

    public async onShowSegments(): Promise<void> {
        if (this.segments.length === 0) {
            await this.loadSegments('');
        }
    }

    public async save(componentAnchorKey: number, changeRequestKey: number): Promise<void> {
        const validationMessage = this.getValidationMessage();
        if (validationMessage) {
            throw new Error(validationMessage);
        }

        const { newRows, changedRows } = getRowsToSave(this.rows);
        if (newRows.length === 0 && changedRows.length === 0) {
            return;
        }

        try {
            await Promise.all([
                newRows.length > 0
                    ? this.componentSegmentRelationService.createAnchors(
                          newRows.map((row) =>
                              createComponentSegmentRelationAnchorPayload(
                                  row,
                                  componentAnchorKey,
                                  changeRequestKey
                              )
                          ) as unknown as ComponentSegmentRelationAnchor[]
                      )
                    : Promise.resolve([] as ComponentSegmentRelationAnchor[]),
                changedRows.length > 0
                    ? this.componentSegmentRelationService.createVersions(
                          changedRows.map((row) =>
                              createComponentSegmentRelationVersionPayload(
                                  row,
                                  row.anchorKey,
                                  changeRequestKey
                              )
                          ) as unknown as ComponentSegmentRelationVersion[]
                      )
                    : Promise.resolve([] as ComponentSegmentRelationVersion[]),
            ]);
        } catch (error) {
            this.notificationService.handleError('Failed to save segment relations', error);
            throw error;
        }

        try {
            await refreshPendingChangesCount(this.store, this.pendingChangesService);
        } catch (error) {
            this.notificationService.handleError(
                'Segment relations were saved, but pending changes count could not be refreshed',
                error
            );
        }

        await this.loadByComponentAnchorKey(componentAnchorKey);
    }

    private async ensureSelectedSegmentsLoaded(): Promise<void> {
        const missingKeys = this.rows
            .map((r) => r.segmentAnchorKey)
            .filter(
                (key): key is number => key !== null && !this.segments.some((s) => s.key === key)
            );

        if (missingKeys.length === 0) return;

        try {
            const results = await Promise.all(
                missingKeys.map((key) =>
                    this.segmentService.getAnchorsWithValidVersion({ aKey: key } as never)
                )
            );
            const loaded = results
                .flat()
                .map((s) => ({ ...s, displayName: this.getSegmentLabel(s) }));
            this.segments = [...this.segments, ...loaded];
        } catch (error) {
            this.notificationService.handleError('Failed to load selected segments', error);
        }
    }

    private async loadByComponentAnchorKey(componentAnchorKey: number): Promise<void> {
        try {
            const anchors =
                await this.componentSegmentRelationService.getByComponent(componentAnchorKey);
            this.rows = createRowsFromAnchors(anchors);
        } catch (error) {
            this.notificationService.handleError('Failed to load segment relations', error);
            this.rows = [];
        }
    }

    private async loadSegments(searchString: string): Promise<void> {
        this.segmentsIsLoading = true;
        try {
            const result = await this.segmentService.getAnchorsWithValidVersion({
                qDisplayName: searchString || undefined,
                qLimit: this.segmentLimit,
                qOffset: 0,
            });
            this.segments = result.map((s) => ({
                ...s,
                displayName: this.getSegmentLabel(s),
            }));
            this.segmentsHasMore = result.length === this.segmentLimit;
        } catch (error) {
            this.notificationService.handleError('Failed to load segments', error);
        } finally {
            this.segmentsIsLoading = false;
        }
    }

    private removeUnsavedRow(row: ComponentSegmentRelationRow): void {
        this.rows = this.rows.filter((r) => r.rowId !== row.rowId);
        this.rowsChanged.emit();
    }
}
